import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Alert,
  Stack,
  Box,
  Divider,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  FormHelperText,
  IconButton
} from '@mui/joy';
import {
  Lock as LockIcon,
  Envelope as EmailIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@phosphor-icons/react';
import api, { authApi } from '../../lib/api';

export const PasswordChangeForm: React.FC = () => {
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger' | 'warning'; text: string } | null>(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  
  // Password strength indicators
  const passwordStrength = (password: string) => {
    if (!password) return { strength: 0, text: '', color: 'neutral' as const };
    
    let strength = 0;
    if (password.length >= 12) strength++;
    if (password.match(/[a-z]+/)) strength++;
    if (password.match(/[A-Z]+/)) strength++;
    if (password.match(/[0-9]+/)) strength++;
    if (password.match(/[^a-zA-Z0-9]+/)) strength++;
    
    const strengthMap = {
      0: { text: '', color: 'neutral' as const },
      1: { text: 'Very Weak', color: 'danger' as const },
      2: { text: 'Weak', color: 'warning' as const },
      3: { text: 'Fair', color: 'neutral' as const },
      4: { text: 'Good', color: 'primary' as const },
      5: { text: 'Strong', color: 'success' as const }
    };
    
    return { strength, ...strengthMap[strength as keyof typeof strengthMap] };
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePasswordForm = (): string | null => {
    if (!passwordData.current_password) {
      return 'Current password is required';
    }
    if (!passwordData.new_password) {
      return 'New password is required';
    }
    if (passwordData.new_password.length < 12) {
      return 'Password must be at least 12 characters long';
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      return 'Passwords do not match';
    }
    if (passwordData.current_password === passwordData.new_password) {
      return 'New password must be different from current password';
    }
    return null;
  };

  const handleChangePassword = async () => {
    const error = validatePasswordForm();
    if (error) {
      setMessage({ type: 'danger', text: error });
      return;
    }

    try {
      setIsChangingPassword(true);
      setMessage(null);
      
      await api.changePassword(passwordData);
      
      setMessage({
        type: 'success',
        text: 'Password changed successfully'
      });
      
      // Clear form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Failed to change password:', error);
      const errorMessage = error?.responseData?.error || 
                          error?.responseData?.errors?.current_password?.[0] ||
                          error?.responseData?.errors?.new_password?.[0] ||
                          'Failed to change password';
      setMessage({
        type: 'danger',
        text: errorMessage
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handlePasswordResetRequest = async () => {
    if (!resetEmail) {
      setMessage({ type: 'danger', text: 'Please enter your email address' });
      return;
    }

    try {
      setIsRequestingReset(true);
      setMessage(null);

      await authApi.requestPasswordReset(resetEmail);

      setResetModalOpen(false);
      setResetEmail('');
      setMessage({
        type: 'success',
        text: 'If an account exists with this email, a password reset link will be sent.'
      });
      
      // Clear message after 10 seconds
      setTimeout(() => setMessage(null), 10000);
    } catch (error) {
      console.error('Failed to request password reset:', error);
      setMessage({
        type: 'danger',
        text: 'Failed to request password reset. Please try again.'
      });
    } finally {
      setIsRequestingReset(false);
    }
  };

  const strength = passwordStrength(passwordData.new_password);

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 3 }}>
            <Typography level="title-lg" startDecorator={<LockIcon />}>
              Password & Security
            </Typography>
            <Button
              variant="soft"
              size="sm"
              startDecorator={<EmailIcon />}
              onClick={() => setResetModalOpen(true)}
            >
              Reset via Email
            </Button>
          </Box>

          {message && (
            <Alert
              color={message.type}
              sx={{ mb: 3 }}
              startDecorator={message.type === 'success' ? <CheckIcon /> : <WarningIcon />}
              endDecorator={
                <IconButton
                  variant="plain"
                  size="sm"
                  color={message.type}
                  onClick={() => setMessage(null)}
                >
                  ×
                </IconButton>
              }
            >
              {message.text}
            </Alert>
          )}

          <Typography level="body-sm" sx={{ mb: 3 }}>
            Change your password to keep your account secure. Make sure to use a strong password.
          </Typography>

          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Current Password</FormLabel>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                disabled={isChangingPassword}
                placeholder="Enter your current password"
              />
            </FormControl>

            <Divider />

            <FormControl>
              <FormLabel>New Password</FormLabel>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                disabled={isChangingPassword}
                placeholder="Enter a new password (min 12 characters)"
              />
              {passwordData.new_password && (
                <FormHelperText>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: '100px',
                        height: '4px',
                        bgcolor: 'background.level2',
                        borderRadius: 'sm',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(strength.strength / 5) * 100}%`,
                          height: '100%',
                          bgcolor: `${strength.color}.500`,
                          transition: 'width 0.3s'
                        }}
                      />
                    </Box>
                    <Typography level="body-xs" color={strength.color}>
                      {strength.text}
                    </Typography>
                  </Box>
                </FormHelperText>
              )}
            </FormControl>

            <FormControl 
              error={passwordData.confirm_password !== '' && 
                     passwordData.new_password !== passwordData.confirm_password}
            >
              <FormLabel>Confirm New Password</FormLabel>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                disabled={isChangingPassword}
                placeholder="Re-enter your new password"
              />
              {passwordData.confirm_password && 
               passwordData.new_password !== passwordData.confirm_password && (
                <FormHelperText>
                  <Typography level="body-xs" color="danger">
                    Passwords do not match
                  </Typography>
                </FormHelperText>
              )}
            </FormControl>

            <Button
              variant="solid"
              color="primary"
              startDecorator={<LockIcon />}
              onClick={handleChangePassword}
              loading={isChangingPassword}
              disabled={isChangingPassword || !passwordData.current_password || 
                       !passwordData.new_password || !passwordData.confirm_password}
              sx={{ mt: 2 }}
            >
              Change Password
            </Button>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>Password Requirements:</Typography>
            <Typography level="body-sm" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>At least 8 characters long</li>
              <li>Mix of uppercase and lowercase letters recommended</li>
              <li>Include numbers for better security</li>
              <li>Special characters make it even stronger</li>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Password Reset Modal */}
      <Modal open={resetModalOpen} onClose={() => setResetModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Reset Password via Email</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            
            <FormControl>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={isRequestingReset}
              />
            </FormControl>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="solid"
                color="primary"
                onClick={handlePasswordResetRequest}
                loading={isRequestingReset}
                disabled={!resetEmail || isRequestingReset}
                fullWidth
              >
                Send Reset Link
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => {
                  setResetModalOpen(false);
                  setResetEmail('');
                }}
                disabled={isRequestingReset}
                fullWidth
              >
                Cancel
              </Button>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </>
  );
};